import { cn } from '@/lib/utils';

export interface ScoreGaugeProps {
  value: number;
  size?: number;
  label?: string;
  grade?: string | null;
  className?: string;
}

/**
 * 270deg arc gauge. Pure SVG, no canvas. Color shifts by threshold:
 *   >= 80 success teal | >= 60 warning amber | < 60 destructive
 */
export function ScoreGauge({
  value,
  size = 92,
  label = 'SCORE',
  grade,
  className,
}: ScoreGaugeProps) {
  const clamped = Math.max(0, Math.min(100, value));
  const r = size * 0.4;
  const c = size / 2;
  const stroke = 9;
  const circ = 2 * Math.PI * r;
  const arcLen = circ * 0.75;
  const dash = arcLen * (clamped / 100);
  const tone = clamped >= 80 ? 'teal-dark' : clamped >= 60 ? 'amber' : 'destructive';
  const strokeColor =
    tone === 'teal-dark'
      ? 'var(--color-teal-dark)'
      : tone === 'amber'
        ? 'hsl(var(--status-warning))'
        : 'hsl(var(--destructive))';
  const gradeColor =
    tone === 'teal-dark'
      ? 'text-teal-dark'
      : tone === 'amber'
        ? 'text-[hsl(var(--status-warning))]'
        : 'text-destructive';
  return (
    <div
      className={cn('relative shrink-0', className)}
      style={{ width: size, height: size * 0.85 }}
    >
      <svg
        width={size}
        height={size * 0.85}
        viewBox={`0 0 ${size} ${size * 0.85}`}
        style={{ transform: 'rotate(135deg)', transformOrigin: `${c}px ${c}px` }}
      >
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth={stroke}
          strokeDasharray={`${arcLen} ${circ}`}
          strokeLinecap="round"
        />
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke={strokeColor}
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray .6s cubic-bezier(.4,0,.2,1)' }}
        />
      </svg>
      <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-0"
        style={{ paddingTop: size * 0.06 }}
      >
        <div className="font-bold text-foreground leading-none" style={{ fontSize: size * 0.3 }}>
          {Math.round(clamped)}
        </div>
        <div className="text-[10px] font-semibold text-muted-foreground tracking-[0.08em] mt-0.5">
          {label}
        </div>
        {grade && (
          <div className={cn('text-[10.5px] font-semibold mt-0.5', gradeColor)}>Grade {grade}</div>
        )}
      </div>
    </div>
  );
}
